import React, {
  useRef,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { Animated, Easing, Modal, View, Platform } from 'react-native';

import { isFunction as _isFunction, get as _get } from 'lodash';

import { Colors } from '../../../Components/Themes';
import { SplashTrackingManager } from '../../../Utils/TrackingManagers';

import FadingSplash from './FadingSplash';
import MoveSplash from './MoveSplash';
import styles from './styles';

const SplashModes = Object.freeze({
  TRANSITION: 'transition',
  FADING: 'fading',
});

const renderModal = ({
  isVisible,
  setIsModalShown,
  mode,
  splashRef,
  splashColor,
  color,
  logoSize,
  isEndAnimationStart,
}) => {
  return (
    <Modal
      animated={false}
      animationType="none"
      visible={isVisible}
      transparent
      onShow={() => setIsModalShown(true)}
    >
      {mode === SplashModes.TRANSITION ? (
        <MoveSplash ref={splashRef} color={splashColor} logoColor={color} logoSize={logoSize} />
      ) : (
        <View
          style={[
            styles.fadeOuterContainer,
            {
              backgroundColor: isEndAnimationStart ? Colors.transparent : splashColor,
            },
          ]}
        >
          <FadingSplash ref={splashRef} color={splashColor} />
        </View>
      )}
    </Modal>
  );
};

const renderAnimatedView = (mode, styleAnimation, wrappedComponent) => {
  return (
    <Animated.View
      style={[
        styles.wrappedComponentContainer,
        mode === SplashModes.FADING ? styleAnimation : null,
      ]}
    >
      {wrappedComponent}
    </Animated.View>
  );
};

const withSplash = (WrappedComponent, config = {}) =>
  forwardRef((props, ref) => {
    const {
      mode: splashMode = SplashModes.FADING,
      color: splashColor = Colors.vfRed,
      defaultTime = 3000,
      logoSize: splashLogoSize = 40,
    } = config;

    const splashRef = useRef(null);
    const wrappedComRef = useRef(null);
    const timeOut = useRef(false);

    const [scaleAnim] = useState(new Animated.Value(0.8));
    const [mode, sethMode] = useState(splashMode);
    const [color, setColor] = useState(Colors.white);
    const [logoSize, setLogoSize] = useState(splashLogoSize);
    const [animationConfig, setAnimationConfig] = useState({});
    const [isVisible, setIsVisible] = useState(true);
    const [isEndAnimationStart, setIsEndAnimationStart] = useState(false);
    const [isModalShown, setIsModalShown] = useState(false);

    const endSplash = () => {
      setTimeout(() => {
        clearTimeout(timeOut.current);
        setIsVisible(false);
      }, 0);
    };

    const startSplash = (configuration = {}) => {
      clearTimeout(timeOut.current);
      setIsVisible(true);
      setAnimationConfig(configuration);
      if (mode === SplashModes.FADING) {
        wrappedComponentAnimation(configuration.startAfter).start();
      }
      setIsEndAnimationStart(true);
      const startSplashFun = _get(splashRef, 'current.startSplash');
      if (_isFunction(startSplashFun)) {
        return startSplashFun(configuration, endSplash);
      }
    };

    const setSplashMode = (desiredMode = SplashModes.FADING) => {
      if (desiredMode === SplashModes.FADING || desiredMode === SplashModes.TRANSITION) {
        sethMode(desiredMode);
      }
    };

    const setSplashColor = (desiredColor = Colors.vfRed) => {
      setColor(desiredColor);
    };

    const wrappedComponentAnimation = (startAfter = 1000) =>
      Animated.timing(scaleAnim, {
        toValue: 1,
        delay: startAfter + 300,
        duration: 400,
        easing: Easing.inOut(Easing.bezier(0.26, 1.0, 0.48, 1.0)),
        useNativeDriver: true,
      });

    const editLogoPosition = logo => {
      if (
        mode === SplashModes.TRANSITION &&
        splashRef.current &&
        _isFunction(splashRef.current.setLogoPosition)
      ) {
        splashRef.current.setLogoPosition(logo);
      }
    };
    useEffect(() => {
      SplashTrackingManager.trackView();
    }, []);
    useImperativeHandle(ref, () => wrappedComRef.current);

    useLayoutEffect(() => {
      timeOut.current = setTimeout(() => {
        const endSplashFunction = _get(splashRef, 'current.endSplash', null);
        endSplashFunction && _isFunction(endSplashFunction) && endSplashFunction();
      }, defaultTime);
      return () => {
        clearTimeout(timeOut.current);
      };
    }, []);

    const duration =
      (animationConfig.startAfter || 1000) + (mode === SplashModes.TRANSITION ? 300 : 800);

    const wrappedComponent = (
      <WrappedComponent
        ref={wrappedComRef}
        startSplashEndingAnimation={startSplash}
        dismissSplash={endSplash}
        setSplashMode={setSplashMode}
        setSplashColor={setSplashColor}
        setSplashLogoSize={setLogoSize}
        endingDuration={duration}
        setSplashLogoPosition={editLogoPosition}
        isVisible={isVisible}
        {...props}
      />
    );
    const styleAnimation = { transform: [{ scale: scaleAnim }] };

    return (
      <View style={Platform.OS === 'android' || isModalShown ? styles.screenHiddenContainer : null}>
        {renderModal({
          isVisible,
          setIsModalShown,
          mode,
          splashRef,
          splashColor,
          color,
          logoSize,
          isEndAnimationStart,
        })}

        {renderAnimatedView(mode, styleAnimation, wrappedComponent)}
      </View>
    );
  });

export default withSplash;
export { SplashModes };
